import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React, { useState } from "react";
import { sendMessageToRecipients } from "../../../../redux/actions";
import { useDispatch } from "react-redux";
import { RecipientsViewModel } from "../../../../domain/RecipientsViewModel";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { buildDefinitions } from "../../activityfeed/buildDefinitions";
import {
  ActivityFeed,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import { InformationRequest } from "../../../../domain/InformationRequest";
import { requestTypeLookupService } from "../../../../services";
import { ActivityType } from "../../../../domain/ActivityType";
import { QueryParams } from "@ofqual/portal-core/core/types";
import WarningIcon from "@material-ui/icons/Warning";
import { LegalDisclaimerMessageDialog } from "../dialogs/LegalDisclaimerDialog";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const CreateMessageDialog: React.FC<{
  recipients: RecipientsViewModel;
  onClose: () => void;
  informationRequest: InformationRequest;
}> = ({ onClose, recipients, informationRequest }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      files: [],
    },
  });

  const handleCreateAction = (data: { files: File[]; message: string }) => {
    dispatch(
      sendMessageToRecipients(recipients.id, data.files, {
        message: data.message,
        recipients: recipients.orgs
          .filter((org) => org.isSelected)
          .map((org) => ({
            organisationRecipientId: org.organisationRecipientId,
            organisationId: org.id,
            organisationName: org.name,
          })),
      })
    );
    onClose();
  };

  const includeActivityTypes = {
    activityTypes: [
      ActivityType.InformationRequestRecipientMessage,
      ActivityType.InformationRequestMessageSent,
      ActivityType.InformationRequestMessageSentAO,
      ActivityType.InformationRequestRecipientMessageAO,
      ActivityType.InformationRequestRespondToMessageAction,
    ],
  };

  const [displayLegalDisclaimer, setDisplayLegalDisclaimer] = useState(false);
  const onCloseLegalDisclaimer = () => {
    setDisplayLegalDisclaimer(false);
  };
  const onLegalDisclaimerClick = () => {
    setDisplayLegalDisclaimer(true);
  };
  const [params] = useState<QueryParams | undefined>(includeActivityTypes);

  const [requestTypeLookups] = useLookup(requestTypeLookupService);

  // build our definintions cast to any because ActivityFeedItem is a little fussy
  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => {},
    informationRequest,
    requestTypeLookups
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Send Message</DialogTitle>
      <Divider />
      <DialogContent style={{ minHeight: "380px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  If you are unable to provide the information or cannot provide
                  it within the specified timescale you should notify Ofqual as
                  soon as possible, explaining why it cannot be provided.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  Unless specifically requested you should redact personal
                  information from any documents provided.
                </ListItemText>
              </ListItem>
            </Grid>

            <Grid item xs={12}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <>
                      <label>Message:</label>
                      <MarkdownEditor
                        onChange={onChange}
                        value={value}
                        dataTest="send-message-freetext-field"
                      />
                    </>
                  )}
                />
              </Grid>
              {errors.message && <Error>This field is required</Error>}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="files"
                as={
                  <FileInput
                    multiple={true}
                    dataTest="send-message-select-files-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <ListItem>
          <ListItemIcon>
            <WarningIcon color="secondary" />
          </ListItemIcon>
          <Tooltip
            title="Click to see full guidance notes"
            aria-label="See-full-guidance-notes"
          >
            <ListItemText
              onClick={onLegalDisclaimerClick}
              style={{ textDecoration: "underline" }}
            >
              Please note the following important information before sending
            </ListItemText>
          </Tooltip>
        </ListItem>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-action-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleCreateAction)}
          color="primary"
          data-test="confirm-create-action-button"
        >
          Send
        </Button>
      </DialogActions>
      <Divider />
      <ActivityFeed
        resourceId={informationRequest.id}
        definitions={definitions}
        actions={[]}
        params={params}
      />
      <LegalDisclaimerMessageDialog
        open={displayLegalDisclaimer}
        onClose={onCloseLegalDisclaimer}
      />
    </Dialog>
  );
};
